import useOverlay from "@/hooks/common/useOverlay";
import {useTaggingClick} from "@/hooks/common/useTagging";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import {departureStart, getPrice, getTrafficIcon, isGoodsTpFH} from "@COMPONENTS/common/eventUtil";
import {FlagEmpasisCd, FlagEvProgDivi, FlagGoodsTp} from "@COMPONENTS/common/flag/Desktop";
import SharePopup from "@COMPONENTS/common/modal/share/Desktop";
import withWishList from "@COMPONENTS/hoc/withWishList";
import ReviewListPopup from "@COMPONENTS/product/common/popup/desktop/ReviewListPopup";
import ReviewPopup from "@COMPONENTS/product/common/popup/desktop/ReviewPopup";
import * as review from "@COMPONENTS/style/detailpackage/bestreviews";
import * as style from "@COMPONENTS/style/detailpackage/itinerary";
import {GuideSlideImg} from "@COMPONENTS/style/slide";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {IconButton} from "@FRAMEWORK/ui/desktop/button";
import {StarScore} from "@FRAMEWORK/ui/desktop/flag";
import {Flex} from "@FRAMEWORK/ui/desktop/layout";
import {StackListItem} from "@FRAMEWORK/ui/desktop/list";
import {GuideSlide} from "@FRAMEWORK/ui/desktop/slide";
import {SubTitle, Title} from "@FRAMEWORK/ui/desktop/text";
import useSigninUser from "@QUERIES/account/useSigninUser";
import useWishlist from "@QUERIES/essential/customer/useWishlist";
import useReview, {AttachType, ReviewDataType, ReviewType} from "@QUERIES/portal/useReview";
import useReviewSummary, {ReviewSummaryType} from "@QUERIES/portal/useReviewSummary";
import {EventDetail} from "@QUERIES/prdt/event/useEventDetail";
import useEventPrice from "@QUERIES/prdt/event/useEventPrice";
import {GoodsDetail} from "@QUERIES/prdt/goods/useGoodsDetail";
import {GoodsImage} from "@QUERIES/prdt/goods/useGoodsImage";
import {GoodsVideo} from "@QUERIES/prdt/goods/useGoodsVideo";
import {Noimage} from "@yb/ui/desktop/common/Noimage";
import {format} from "@yb/utils";
import dayjs from "dayjs";
import {useRouter} from "next/router";
import {useMemo, useState} from "react";
import {Navigation} from "swiper";
import {Swiper, SwiperSlide} from "swiper/react";
import ImageVideoPop from "./popup/ImageVideoPop";

interface Props {
	eventDetail: EventDetail;
	goodsDetail?: GoodsDetail;
	goodsImage?: GoodsImage[];
	goodsVideo?: GoodsVideo[];
	/** 일정표 사용여부 */
	useScheduleView?: boolean;
	/** 인쇄 사용여부 */
	usePrintView?: boolean;
}

const IconListComponent = (props: Props) => {
	const {eventDetail} = props;
	const {evStaCd, avlilBlockQty} = eventDetail;
	const avlilBlockQtyText = useMemo(() => {
		let result = ``;
		if (evStaCd !== "05") {
			if (avlilBlockQty > 0) {
				result = `잔여 ${avlilBlockQty}석`;
			} else if (avlilBlockQty === 0) {
				result = `잔여석 대기`;
			}
		} else if (avlilBlockQty > 0) {
			result = `잔여 ${avlilBlockQty}석`;
		} else if (avlilBlockQty === 0) {
			result = `잔여석 마감`;
		}
		return result;
	}, [eventDetail]);
	const traffic = getTrafficIcon(eventDetail);

	return (
		<style.ScheduleIconList>
			<li>
				<style.ScheduleIcon icon="schedule" />
				<span>{eventDetail.dayStayInfo.split("박").join("박 ")}</span>
			</li>
			<li>
				<style.ScheduleIcon icon={traffic.icon} />
				{
					<span>
						{traffic.name}
						{traffic.icon === "air-premium" && (
							<>
								<br />
								{eventDetail.seatClassNm}
							</>
						)}
						{eventDetail.stopoverCntYn === "Y" && !(eventDetail.outStovCnt === 0 && eventDetail.inStovCnt === 0) && (
							<>
								<br />
								출국 {eventDetail.outStovCnt > 0 ? `경유${eventDetail.outStovCnt}회` : "직항"}
								<br />
								귀국 {eventDetail.inStovCnt > 0 ? `경유${eventDetail.inStovCnt}회` : "직항"}
							</>
						)}
					</span>
				}
			</li>
			{/* 08:출발예정 ,07: 출발확정, 11: 100%출발확정 */}
			{["07", "08", "11"].includes(eventDetail.evProgDivi) && eventDetail.goodsTp !== "G" && (
				<li>
					<style.ScheduleIcon icon="decide" />
					<span>{eventDetail.evProgDiviNm}</span>
				</li>
			)}
			{/* 가이드 / 인솔자 동행 */}
			<li>
				<style.ScheduleIcon icon="guide" />
				<span>
					{eventDetail.webGuidConfYn === "Y" && eventDetail.webInsolConfYn === "Y" && (
						<>
							가이드포함
							<br />
							인솔자동행
						</>
					)}
					{eventDetail.webGuidConfYn === "N" && eventDetail.webInsolConfYn === "N" && (
						<>
							가이드불포함
							<br />
							인솔자미동행
						</>
					)}
					{eventDetail.webGuidConfYn === "Y" && eventDetail.webInsolConfYn === "N" && <>가이드포함</>}
					{eventDetail.webGuidConfYn === "N" && eventDetail.webInsolConfYn === "Y" && <>인솔자동행</>}
				</span>
			</li>
			{/* 가이드경비 */}
			<li>
				<style.ScheduleIcon icon="expenses" />
				{eventDetail.guideExpAmt ? (
					<span>
						가이드기사
						<br />
						{eventDetail.expCurrCd} {format.koKRFormatNumber(eventDetail.guideExpAmt)}
					</span>
				) : (
					<span>
						가이드기사
						<br />
						경비없음
					</span>
				)}
			</li>
			{/* 남은 좌석 */}
			{!isGoodsTpFH(eventDetail.goodsTp) && eventDetail.goodsTp !== "G" && (
				<li>
					<style.ScheduleIcon icon="seat" />
					<span>
						{avlilBlockQtyText}
						<br />
						최소출발 {eventDetail.minCnt}명
					</span>
				</li>
			)}
			{/* 여행자보험 항목 제외. 표준약관 / 특별약관 텍스트 노출 */}
			<li>
				<style.ScheduleIcon icon="insurance" />
				<span>{eventDetail.webCancelCommiYn === "N" ? "특별약관" : "표준약관"}</span>
			</li>
			{eventDetail.singleChagYn === "Y" && eventDetail.singleChagAmt !== 0 && (
				<li>
					<style.ScheduleIcon icon="charge" />
					<span>
						싱글차지
						<br />
						{format.koKRFormatNumber(eventDetail.singleChagAmt)}원
					</span>
				</li>
			)}
			{eventDetail.freeScheYn === "Y" && (
				<li>
					<style.ScheduleIcon icon="free" />
					<span>
						자유일정
						<br />
						있음
					</span>
				</li>
			)}
			<li>
				<style.ScheduleIcon icon="shopping" />
				<span>쇼핑 {eventDetail.shopCnt > 0 ? `${eventDetail.shopCnt}회` : "없음"}</span>
			</li>
			{eventDetail.greenFeeYn === "Y" && (
				<li>
					<style.ScheduleIcon icon="green" />
					<span>
						그린피
						<br />
						포함
					</span>
				</li>
			)}
			{eventDetail.caddieFeeYn === "Y" && (
				<li>
					<style.ScheduleIcon icon="caddy" />
					<span>
						캐디피
						<br />
						포함
					</span>
				</li>
			)}
		</style.ScheduleIconList>
	);
};

/** 픽토그램 */
const IconList = (props: Props) => (
	<ErrorBoundary logMsg="DetailHeader IconList Error">
		<IconListComponent {...props} />
	</ErrorBoundary>
);

const ReviewSummary = (props: {goodsCd: string; reviewSummary: ReviewSummaryType; reviewData: ReviewDataType}) => {
	const {reviewData, reviewSummary, goodsCd} = props;

	const originReview: (ReviewType & {attachList: AttachType[]})[] = useMemo(() => {
		const {reviewList, attachList: attach} = reviewData;
		return reviewList.reduce((acc, cur) => {
			const attachList = attach.filter(v => v.reviewId === cur.reviewId);
			const item = {...cur, attachList};
			acc.push(item);
			return acc;
		}, []);
	}, [reviewData]);

	/** 리뷰 조건(사진/영상 리뷰 3개이상 10개까지 노출, 최신리뷰 별점 높은순) */
	const reviews = useMemo(
		() =>
			originReview
				.filter(v => v.attachList?.length > 0 && v.score >= 4) // 사진/영상 리뷰, 4점 이상
				.sort((a, b) => dayjs(b.reviewDt).valueOf() - dayjs(a.reviewDt).valueOf()) // 최신 리뷰 순
				.sort((a, b) => b.score - a.score) // 별점 높은순
				.slice(0, 10), // 10개까지 노출
		[originReview],
	);

	// 상세 팝업
	const [detailReviewId, setDetailReviewId] = useState<number>(null);
	const handleReviewOpen = (reviewId: number) => setDetailReviewId(reviewId);
	const handleReviewClose = () => setDetailReviewId(null);

	// 리스트 팝업
	const [isListPopup, setIsListPopup] = useState(false);
	const handleReviewListOpen = () => setIsListPopup(true);
	const handleReviewListClose = () => setIsListPopup(false);

	const handleScroll = () => {
		const reviewsElement = document.getElementById("reviews");

		if (reviewsElement) {
			reviewsElement.click();

			const observer = new MutationObserver((mutations, obs) => {
				if (reviewsElement.offsetHeight > 0) {
					reviewsElement.scrollIntoView({behavior: "smooth"});
					obs.disconnect();
				}
			});

			observer.observe(document.body, {childList: true, subtree: true});
		}
	};

	return (
		<>
			{reviews.length > 2 && (
				<review.Wrap>
					<review.SubTitleWrap onClick={handleScroll}>
						<SubTitle
							onClick={() => {
								const link = document.querySelector('a[href="#content3"]') as HTMLAnchorElement | null;
								if (link) link.click();
							}}
						>
							<review.Rating>
								<span>{reviewSummary.scoreAvg ?? " - "}</span>
							</review.Rating>
							{`실제 여행객 ${reviewSummary.reviewCnt}명의 리뷰 보기`}
						</SubTitle>
						<review.ArrowIcon />
					</review.SubTitleWrap>
					<review.SlideWrap>
						{reviews.length > 3 && (
							<review.SlideButtonWrap>
								<>
									<review.SlideButton className="btn-prev" direction="prev" />
									<review.SlideButton className="btn-next" direction="next" />
								</>
							</review.SlideButtonWrap>
						)}
						<Swiper
							slidesPerView={3}
							spaceBetween={20}
							navigation={{
								nextEl: ".btn-next",
								prevEl: ".btn-prev",
							}}
							modules={[Navigation]}
						>
							{reviews.map((item, index) => {
								const {custNm, detail, score, reviewDt, reviewId} = item;
								return (
									<SwiperSlide key={index}>
										<review.SlideContent onClick={() => handleReviewOpen(reviewId)}>
											<review.ScoreWrap>
												<StarScore>
													<span style={{width: `calc(${score}*20%)`}} />
												</StarScore>
												<review.Score>{score}</review.Score>
											</review.ScoreWrap>
											<review.Content dangerouslySetInnerHTML={{__html: detail.replace(/\n/g, "<br />")}} />
											<review.MetaInfo>
												<review.UserName>{custNm}님</review.UserName>
												<review.Date>{dayjs(reviewDt).format("YYYY.MM.DD")}</review.Date>
											</review.MetaInfo>
										</review.SlideContent>
									</SwiperSlide>
								);
							})}
						</Swiper>
					</review.SlideWrap>
				</review.Wrap>
			)}
			{/* 리뷰 상세 팝업 */}
			{detailReviewId && (
				<ReviewPopup
					goodsCd={goodsCd}
					reviewId={detailReviewId}
					close={handleReviewClose}
					listOpen={handleReviewListOpen}
				/>
			)}
			{/* 리뷰 리스트 팝업 */}
			{isListPopup && <ReviewListPopup goodsCd={goodsCd} close={handleReviewListClose} reviewOpen={handleReviewOpen} />}
		</>
	);
};

const reviewSummaryDefault = {
	reviewCnt: 0,
	scoreAvg: 0,
	companion: "",
	companionNm: "",
	companionAttr1: "",
	companionAttr2: "",
	keywordList: [],
};

const DetailHeaderComponent = (props: Props) => {
	const {eventDetail, goodsDetail, goodsImage, goodsVideo, useScheduleView = false, usePrintView = false} = props;
	const router = useRouter();
	const dspSid = router.query?.dspSid as string;
	const menu = router.query?.menu as string;
	/** 로그인 정보 */
	const useSign = useSigninUser();
	const signinCode = useSign.data?.code || "9999";
	const webCustNo = useSign.data?.body.webCustNo || "";

	/** 리뷰 서머리 정보 */
	const reviewSummaryFetch = useReviewSummary(eventDetail.goodsCd);
	const reviewSummary = reviewSummaryFetch.data?.body || reviewSummaryDefault;

	/** 리뷰 리스트 조회 */
	const reviewDataFetch = useReview(eventDetail.goodsCd);
	const reviewData = reviewDataFetch.data?.body || null;

	/** 위시리스트 */
	const useWish = useWishlist(webCustNo, {enabled: !!webCustNo, throwOnError: false});
	const wishlist = useWish.data?.body?.wishProductsResultList || [];
	const isWishNo = wishlist.filter(wish => wish.evCd === eventDetail.evCd)?.at(0)?.productWishId || "";
	const eventPriceFetch = useEventPrice(eventDetail.evCd);
	const price = getPrice(eventPriceFetch.data?.body);

	const WishList = withWishList(prop => (
		<span>
			<IconButton icon="wishList" {...prop}>
				찜
			</IconButton>
		</span>
	));

	// 이미지 상세 팝업
	const [open] = useOverlay();
	const handleGoodsImageVideo = () => {
		open(prop => <ImageVideoPop {...prop} images={goodsImage} videos={goodsVideo} title="여행지 이미지 상세 보기" />);
	};

	// 공유하기
	const handleShare = () => {
		open(prop => <SharePopup {...prop} parameter={`menu=${menu}&dspSid=${dspSid}&evCd=${eventDetail.evCd}`} />);
	};

	// 문의하기
	const hanldeQna = () => {
		const path = `${publicRuntimeConfig.ENV_PACKAGE_URL}/cs/qnaWrite.yb?evCd=${eventDetail.evCd}`;
		if (signinCode === "0000") {
			location.href = path;
		} else {
			const text = "로그인 후 이용하실 수 있습니다. <br/>로그인하시겠습니까?";
			MessageUtil.confirm(text, () => LoginUtil.login(path));
		}
	};

	/** 인쇄하기 */
	const handlePrint = () => {
		window.open(`/product/schedule/print?evCd=${eventDetail.evCd}`);
	};

	const {taggingClick} = useTaggingClick("pkg_select_evCd_wish", eventDetail);

	return (
		<>
			<style.Wrap>
				<Flex justify="space-between">
					<Flex>
						<Flex gap="3" align="center">
							<FlagGoodsTp goodsTp={eventDetail.goodsTp} />
							<FlagEmpasisCd emphasisCd={eventDetail.emphasisDivi} />
							<FlagEvProgDivi evProgDivi={eventDetail.evProgDivi} evProgDiviNm={eventDetail.evProgDiviNm} />
						</Flex>
						<style.ProductNumber>
							<StackListItem>
								상품번호<style.TextNumber>{eventDetail.evCd}</style.TextNumber>
							</StackListItem>
							{!useScheduleView && (
								<StackListItem>
									<a
										href="#"
										onClick={e => {
											e.preventDefault();
											const link = document.querySelector('a[href="#content3"]') as HTMLAnchorElement | null;
											if (link) link.click();
										}}
									>
										<span>{reviewSummary?.scoreAvg > 0 ? reviewSummary?.scoreAvg : " - "}</span>
										{`/5 (리뷰 ${reviewSummary?.reviewCnt ?? 0}건)`}
									</a>
								</StackListItem>
							)}
						</style.ProductNumber>
					</Flex>

					{!useScheduleView && (
						<style.ButtonBox>
							<span>
								<IconButton icon="enquire" onClick={hanldeQna}>
									문의
								</IconButton>
							</span>
							<span>
								<IconButton icon="print" onClick={handlePrint}>
									인쇄
								</IconButton>
							</span>
							{/* 위시리스트 */}
							<WishList
								isActive={!!isWishNo}
								wishNo={isWishNo}
								diviCd="E"
								evCd={eventDetail.evCd}
								link={`/product/detailPackage?menu=${menu}&dspSid=${dspSid}&evCd=${eventDetail.evCd}`}
								addConfirm={() => taggingClick({wishYn: "Y"})}
								removeConfirm={() => taggingClick({wishYn: "N"})}
							/>
							<span>
								<IconButton icon="share" onClick={handleShare}>
									공유
								</IconButton>
							</span>
						</style.ButtonBox>
					)}
				</Flex>
				<style.HeaderWrap>
					<Title>{eventDetail.evNm}</Title>
					<style.SummaryWrap>
						<dl>
							<dt>출발</dt>
							<dd>{departureStart(eventDetail)}</dd>
						</dl>
						<dl>
							<dt>성인</dt>
							<dd>{format.koKRFormatNumber(price?.adult)} 원</dd>
						</dl>
					</style.SummaryWrap>
				</style.HeaderWrap>
				{!usePrintView && (
					<style.SlideWrap>
						<GuideSlide perView={3} spaceBetween={15}>
							{goodsVideo?.map((item, index) => (
								<SwiperSlide key={index}>
									<GuideSlideImg types="movie" onClick={handleGoodsImageVideo}>
										<Noimage width="100%" height="271px" src={item.vimeoThum1} alt={item.vimeoNote} />
									</GuideSlideImg>
								</SwiperSlide>
							))}
							{goodsImage.map((item, index) => (
								<SwiperSlide key={index}>
									<GuideSlideImg onClick={handleGoodsImageVideo}>
										<Noimage width="100%" height="271px" src={item.imageThum3} alt={item.imageNote} />
									</GuideSlideImg>
								</SwiperSlide>
							))}
						</GuideSlide>
					</style.SlideWrap>
				)}
				{eventDetail.noTosYn === "Y" && (
					<style.OptionBox>
						<p>
							모든 경비가 포함된 <strong>NO쇼핑/NO옵션/NO팁</strong> 상품입니다.
						</p>
					</style.OptionBox>
				)}
				{/* 픽토그램 */}
				<IconList eventDetail={eventDetail} />
				{goodsDetail?.goodTravelYn === "Y" && (
					<style.CertifiedBox>
						<style.CertifiedBanner bgColor="#fbf4e0">
							<p>
								해당 상품은 <strong>우수여행상품으로 인증된 상품</strong>입니다.
							</p>
						</style.CertifiedBanner>
						<style.CertifiedContent>
							<p>
								우수여행상품이란 소비자에게 여행상품의 선택기준을 마련하고, 여행상품의 국제 경쟁력 제고를 위하여
								한국여행업협회가 주관하고 문화체육관광부가 후원하여 인증한 상품입니다.
							</p>
						</style.CertifiedContent>
					</style.CertifiedBox>
				)}
				{eventDetail.goodsTp === "K" && eventDetail.trTp === null && (
					<style.CertifiedBox>
						<style.CertifiedBanner noIcon>
							<p>
								해당 상품은 여행지까지의 <strong>교통편(항공,선박 등)이 불포함 된 상품</strong>입니다.
							</p>
						</style.CertifiedBanner>
						<style.CertifiedContent>
							<p>
								숙박+현지투어(차량포함)+식사만 포함된 상품입니다. 여행지까지의 교통편은 개별 진행 부탁드리며, 도움이
								필요하실 경우 노랑풍선으로 문의바랍니다.
							</p>
						</style.CertifiedContent>
					</style.CertifiedBox>
				)}
				{eventDetail.webInsolConfYn === "Y" && (
					<style.CertifiedBox>
						<style.CertifiedBanner noIcon>
							<p>
								해당 상품은 <strong>인솔자가 동행하는 상품</strong>입니다.
							</p>
						</style.CertifiedBanner>
						<style.CertifiedContent>
							<p>인솔자 동행하는 상품은 상품가에 인솔자 경비(가이드, 기사 경비 외)가 모두 포함되어 있습니다.</p>
						</style.CertifiedContent>
					</style.CertifiedBox>
				)}
				{/* 리뷰 서머리 */}
				{reviewSummary && reviewData && (
					<ReviewSummary goodsCd={eventDetail.goodsCd} reviewSummary={reviewSummary} reviewData={reviewData} />
				)}
			</style.Wrap>
		</>
	);
};

const DetailHeader = (props: Props) => (
	<ErrorBoundary logMsg="DetailHeader Error">
		<DetailHeaderComponent {...props} />
	</ErrorBoundary>
);

export default DetailHeader;
